// Admin copy of the web app's src/Organizer/AwardsTable/awardsSearch.js (card f65ae884).
// Keep the two in step; only this header differs.
// Awards Table search and display rules (card f65ae884). Pure functions, no
// React, so they are unit-tested on their own. The admin app carries a
// verbatim copy: keep the two in step.

// 1st, 2nd, 3rd, 4th ... 11th, 12th, 13th ... 21st, 111th
export const ordinal = (n) => {
  const v = Number(n);
  if (!Number.isInteger(v) || v < 1) return '';
  const t = v % 100;
  if (t >= 11 && t <= 13) return `${v}th`;
  return `${v}${{ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th'}`;
};

// lower case, accents removed (Ní -> ni), single spaces
export const normalize = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const words = (s) => normalize(s).split(' ').filter(Boolean);

// Does the typed name match this dancer? The query must be the start of the
// first name, the last name or the full name, or every typed word must start
// some word of the name ("aoife b" -> Aoife Byrne, "ni bh" -> Ní Bhriain).
export const nameMatches = (dancer, query) => {
  const q = normalize(query);
  if (!q) return false;
  const full = normalize(`${dancer.fname} ${dancer.lname}`);
  if ([normalize(dancer.fname), normalize(dancer.lname), full].some((s) => s && s.startsWith(q))) return true;
  const nameWords = words(full);
  return q.split(' ').every((w) => nameWords.some((nw) => nw.startsWith(w)));
};

// { kind: 'empty' | 'card' | 'names' | 'none', results }
// A number is a card number and matches exactly (normally one dancer; two if
// the data gives two dancers the same card). Anything else is a name.
export const searchDancers = (dancers, query) => {
  const q = String(query ?? '').trim();
  if (!q) return { kind: 'empty', results: [] };
  if (/^\d+$/.test(q)) {
    const card = Number(q);
    const results = (dancers || []).filter((d) => d.card === card);
    return { kind: results.length ? 'card' : 'none', results };
  }
  const results = (dancers || []).filter((d) => nameMatches(d, q));
  return { kind: results.length ? 'names' : 'none', results };
};

// The part of `text` the query matched, for highlighting: [before, match, after]
// (match empty when nothing lines up).
export const highlight = (text, query) => {
  const t = String(text ?? '');
  const q = normalize(query);
  if (!q) return [t, '', ''];
  const plain = normalize(t);
  const at = plain.indexOf(q.split(' ')[0]);
  if (at !== 0 && !(at > 0 && plain[at - 1] === ' ')) return [t, '', ''];
  // normalize() keeps one character per letter for these names, so indexes line up
  const len = q.split(' ')[0].length;
  return [t.slice(0, at), t.slice(at, at + len), t.slice(at + len)];
};

const isCollectable = (e) => e.status === 'placed';

// Counts for a dancer's status pill and the warnings.
export const dancerSummary = (dancer) => {
  const entries = dancer.entries || [];
  const placed = entries.filter(isCollectable);
  const toCollect = placed.filter((e) => !e.collected).length;
  const pending = entries.filter((e) => e.status === 'pending').length;
  // marked collected, but a correction since means no place
  const mismatched = entries.filter((e) => e.collected && !isCollectable(e)).length;
  let label;
  let tone;
  if (toCollect > 0) {
    label = `${toCollect} to collect`;
    tone = 'todo';
  } else if (placed.length > 0) {
    label = 'All collected';
    tone = 'done';
  } else if (pending > 0) {
    label = 'Results pending';
    tone = 'wait';
  } else {
    label = 'Nothing to collect';
    tone = 'none';
  }
  return { placed: placed.length, toCollect, pending, mismatched, label, tone };
};

const RANK = { placed: 0, pending: 1, not_placed: 2, no_score: 3 };

// Placed first (best place first), then results pending, then no place / did
// not dance; ties by competition code (U9-2 before U9-10).
export const orderEntries = (entries, competitionsById) => {
  const code = (e) => competitionsById?.[String(e.competitionId)]?.code || '';
  return [...(entries || [])].sort(
    (a, b) =>
      (RANK[a.status] ?? 9) - (RANK[b.status] ?? 9) ||
      (a.status === 'placed' ? a.place - b.place : 0) ||
      code(a).localeCompare(code(b), undefined, { numeric: true })
  );
};

// One entry's collected state changed: a new dancers array (the rest shared).
export const withCollected = (dancers, dancerId, competitionId, collected) =>
  (dancers || []).map((d) =>
    String(d._id) !== String(dancerId)
      ? d
      : {
          ...d,
          entries: d.entries.map((e) =>
            String(e.competitionId) === String(competitionId) ? { ...e, collected } : e
          ),
        }
  );
